import type { HeaderCardType } from '@design-system/components/Header/HeaderListView.props'

/**
 * Fetches image logo bottom bar content from the CMS API with error handling.
 *
 * @async
 * @function useImgLogoBottomBar
 * @param {string} deliveryKey - The unique key used to retrieve the specific image logo bottom bar content.
 * @returns {Promise<HeaderCardType[] | null>} An array of header card items or null if retrieval fails.
 *
 * @description
 * This composable function retrieves image logo bottom bar content from the CMS API with the following features:
 * - Uses the current locale path
 * - Includes optional query parameters like 'vse' and 'contentId' from the current route
 * - Transforms the response into an array of header card items
 * - Provides fallback to null if content retrieval fails
 *
 * @example
 * // Fetch image logo bottom bar content
 * const logoItems = await useImgLogoBottomBar('bottom_bar_logos')
 *
 * @note
 * - Returns an empty array if no content items are found
 * - Returns null if an error occurs during content retrieval
 */
export async function useImgLogoBottomBar(deliveryKey: string) {
  const localePath = useLocalePath()

  const query = {
    locale: localePath(''),
    vse: useRoute().query.vse,
    contentId: useRoute().query.contentId,
    deliveryKey: deliveryKey,
  }

  try {
    const { data: imgLogoContentResponse } = await useFetch(
      '/api/cms/getImgLogoBottomBar',
      {
        query,
      }
    )

    const imgLogoTransform: HeaderCardType[] =
      imgLogoContentResponse.value?.content.listOfContentItems ?? []

    return imgLogoTransform
  } catch {
    return null
  }
}
